// src/lib/sheetNaming.mjs
// ───────────────────────────────────────────────────────────────────────────
// 🪪 Identidad única de plancha (PLAN-TERMINAL-OPERADOR §9.3 Pilar 1)
//
// ÚNICA fuente del nombre de archivo/HRI de una plancha. El criterio (decisión
// de Daniel, §9.2) es el FORMATO VIEJO = nombre de archivo frontal:
//     {JOB}[-{BLOQUE}]_{NNN}{N|P|A}      ej: 1116-COR_002P · 1123_001N
// Sufijo: N = solo corte · P = impresión 1 cara · A = 2 caras (el dorso es B).
//
// Es .mjs (ESM puro, sin dependencias) a propósito: lo importan el SheetModal
// (CRA/webpack), los unit tests y el script de migración Node
// (scripts/migrate-hri-formato-viejo.mjs) — un solo cálculo para los tres.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Nombres de archivo (frente/dorso) de una plancha. PURO.
 * El FRENTE es también el `hri` (identidad visible de la plancha).
 *
 * @param {object} p
 * @param {string} p.jobId        id del job, con o sin prefijo "J-" (ej. "J-1116")
 * @param {string} [p.blockCode]  código del bloque ('' o null si el job no agrupa)
 * @param {number|string} p.sheetNumber  número de plancha (se rellena a 3 dígitos)
 * @param {boolean} p.isPrinting  ¿lleva impresión?
 * @param {number|string} [p.printFaces]  1 | 2 (solo si isPrinting)
 * @return {{front: string, back: string}}  '' si faltan datos
 */
export function computeSheetNames({ jobId, blockCode, sheetNumber, isPrinting, printFaces }) {
  const num = Number(sheetNumber);
  const jobNumber = String(jobId || '').replace(/^J-/, '');
  if (!jobNumber || !num) return { front: '', back: '' };

  const plate = String(num).padStart(3, '0');
  const code = (blockCode || '').trim();
  const baseName = code ? `${jobNumber}-${code}_${plate}` : `${jobNumber}_${plate}`;

  if (!isPrinting) return { front: `${baseName}N`, back: '' };
  // printFaces llega a veces como string desde el formulario → comparación numérica.
  if (Number(printFaces) === 2) return { front: `${baseName}A`, back: `${baseName}B` };
  return { front: `${baseName}P`, back: '' };
}

/**
 * Código de bloque a partir del job y el nombre de bloque guardado en la plancha.
 * Mismo fallback que usa la UI (SheetModal/SheetDetailModal) para bloques legados
 * o renombrados: el nombre en MAYÚSCULAS con espacios → '_'.
 *
 * @param {object} job   doc del job ({ blocks: [{name, code}] })
 * @param {string} blockName  valor `block` guardado en la plancha ('' si no agrupa)
 * @return {string} '' si la plancha no pertenece a un bloque
 */
export function resolveBlockCode(job, blockName) {
  const name = (blockName || '').trim();
  if (!name) return '';
  const b = (job?.blocks || []).find((x) => x?.name === name);
  return ((b?.code || '').trim()) || name.replace(/\s+/g, '_').toUpperCase();
}

/**
 * Extrae el NÚMERO de plancha (NNN) embebido en un hri / nombre de archivo frontal.
 * Formato {JOB}[-{BLOQUE}]_{NNN}{N|P|A}: el número son los dígitos tras el ÚLTIMO
 * '_', con un sufijo de tipo opcional (una letra). Devuelve number o null.
 * Lo usa la guarda del invariante D5 (sheetNumber ≡ número del hri, §9.4).
 *
 * @param {string} hri
 * @return {number|null}
 */
export function extractSheetNumber(hri) {
  const m = String(hri || '').match(/_(\d+)[A-Za-z]?$/);
  return m ? Number(m[1]) : null;
}

/**
 * Normaliza un hri (o texto tecleado/escaneado) para el campo de búsqueda
 * `hriSearch`: minúsculas, sin `#` inicial, sin espacios en los bordes.
 * La búsqueda es case-insensitive POR DISEÑO (§9.3 Pilar 1), no por trucos.
 *
 * @param {string} text
 * @return {string}
 */
export function normalizeHriSearch(text) {
  return String(text || '').trim().replace(/^#/, '').toLowerCase();
}
